import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import { PageShell } from '../../components/layout/page/PageShell';
import { portalOverviewByProjectId } from './portalOverviewMock';
import { PortalProject } from './portalTypes';
import { usePortalState } from './PortalState';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

function statusBadgeClass(statusLabel: string): string {
  if (statusLabel.toLowerCase().includes('conclu')) return 'bg-sky-50 text-sky-700';
  return 'bg-emerald-50 text-emerald-700';
}

function isActiveProject(project: PortalProject): boolean {
  const status = project.statusLabel.toLowerCase();
  if (
    status.includes('conclu') ||
    status.includes('cancel') ||
    status.includes('paus') ||
    status.includes('encerr')
  ) {
    return false;
  }

  return project.active !== false && project.started;
}

export function PortalProjectsPage() {
  const { data } = usePortalState();
  const [activeOnly, setActiveOnly] = useState(true);
  const projects = activeOnly ? data.projects.filter(isActiveProject) : data.projects;

  return (
    <PageShell>
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Meus Projetos</h1>
          <p className="mt-1 text-sm text-secondary-600">
            Acompanhe o andamento dos projetos contratados com a Baruk.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              role="switch"
              aria-checked={activeOnly}
              aria-label="Exibir apenas projetos ativos"
              title={activeOnly ? 'Exibindo apenas projetos ativos' : 'Exibindo todos os projetos da empresa'}
              className={`erp-switch ${activeOnly ? 'erp-switch-on erp-switch-allow' : ''}`}
              onClick={() => setActiveOnly((current) => !current)}
            />
            <span className="text-sm text-secondary-700">Apenas projetos ativos</span>
          </div>
          <p className="text-sm text-secondary-500">
            {projects.length} {projects.length === 1 ? 'projeto' : 'projetos'}
          </p>
        </div>
      </header>

      {projects.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-secondary-300 bg-white px-4 py-12 text-center">
          <FolderIcon className="mx-auto h-10 w-10 text-secondary-300" aria-hidden />
          <p className="mt-3 text-sm font-medium text-secondary-700">
            {activeOnly ? 'Nenhum projeto ativo' : 'Nenhum projeto'}
          </p>
          <p className="mt-1 text-sm text-secondary-500">
            {activeOnly
              ? 'Desligue o filtro para ver os demais projetos da empresa.'
              : 'Quando um projeto for associado à sua empresa, ele aparecerá aqui.'}
          </p>
        </div>
      ) : (
        <ul className="mt-6 grid gap-4 lg:grid-cols-2">
          {projects.map((project) => {
            const overview = portalOverviewByProjectId[project.id];
            return (
              <li key={project.id}>
                <Link
                  to={`/portal/projects/${project.id}`}
                  className="group flex h-full flex-col rounded-2xl border border-secondary-200 bg-white p-5 transition-colors hover:border-primary-300 hover:bg-primary-50/30"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-lg font-semibold text-secondary-900 group-hover:text-primary-800">
                          {project.name}
                        </h2>
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusBadgeClass(project.statusLabel)}`}
                        >
                          {project.statusLabel}
                        </span>
                      </div>
                      <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-secondary-600">
                        {overview?.description ?? project.summary}
                      </p>
                    </div>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-700 transition-colors group-hover:bg-primary-100">
                      <ArrowRightIcon className="h-4 w-4" aria-hidden />
                    </span>
                  </div>

                  <div className="mt-5 grid gap-3 sm:grid-cols-3">
                    <div className="rounded-xl bg-secondary-50 px-3 py-2.5">
                      <p className="flex items-center gap-1.5 text-xs font-medium text-secondary-500">
                        <ChartBarIcon className="h-3.5 w-3.5" aria-hidden />
                        Progresso
                      </p>
                      <p className="mt-1 text-sm font-semibold text-secondary-900">
                        {overview ? `${overview.progressPercent}%` : '—'}
                      </p>
                    </div>
                    <div className="rounded-xl bg-secondary-50 px-3 py-2.5">
                      <p className="flex items-center gap-1.5 text-xs font-medium text-secondary-500">
                        <CalendarDaysIcon className="h-3.5 w-3.5" aria-hidden />
                        Próxima entrega
                      </p>
                      <p className="mt-1 truncate text-sm font-semibold text-secondary-900">
                        {overview?.nextDeliveryDateLabel ?? '—'}
                      </p>
                    </div>
                    <div className="rounded-xl bg-secondary-50 px-3 py-2.5">
                      <p className="text-xs font-medium text-secondary-500">Responsável</p>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-50 text-[10px] font-semibold text-primary-700">
                          {initials(overview?.ownerName ?? project.ownerName)}
                        </span>
                        <p className="truncate text-sm font-semibold text-secondary-900">
                          {overview?.ownerName ?? project.ownerName}
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </PageShell>
  );
}
